import Fuse, { IFuseOptions } from 'fuse.js';
import { action, autorun, computed, IReactionDisposer, observable, reaction, runInAction } from 'mobx';
import { IImageProps, ISuggestionItem } from 'telia-front-react';
import isNumeric from 'validator/es/lib/isNumeric';
import ErrorUtil from '../error/ErrorUtil';
import { CountryWithPrices, CallingCodeSuggestion, PriceByPrefix, ServicePrice } from '../roaming/RoamingInterfaces';
import { CommonAPI } from '../util/ApiUtil';
import { FLAG_IMAGE_BASEURL, getCountryLabel, mapCountryToNumberSuggestion, sortCountries } from '../util/CountryUtil';
import { getFuseResults } from '../util/ObjectUtil';
import { clearWhitespace } from '../util/TextUtil';
import LanguageStore, { Language } from './LanguageStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import { Segment, SEGMENT_PRIVATE } from './SegmentStore';

class RoamingNumberStore {
  @observable accessor numberSuggestionSearchTerm: string = '';
  @observable accessor currentNumberSuggestions: CallingCodeSuggestion[] = [];
  @observable accessor allNumberSuggestions: CallingCodeSuggestion[];
  @observable accessor selectedCountry: CountryWithPrices;
  @observable accessor noResults: boolean = false;
  @observable accessor selectedCountryImage: IImageProps = null;
  @observable accessor scrollToResults = false;

  @observable accessor priceByPrefixList: {
    prices: PriceByPrefix[];
    status: string;
  } = {
    prices: [],
    status: 'UNINITIALIZED'
  };

  @observable accessor countryList: {
    countries: CountryWithPrices[];
    status: string;
  } = {
    countries: [],
    status: 'UNINITIALIZED'
  };

  public navigationReaction: IReactionDisposer = null;
  public fuzzySearch: Fuse<CallingCodeSuggestion>;
  private readonly fuzzySearchOptions: IFuseOptions<CallingCodeSuggestion> = {
    shouldSort: true,
    keys: ['label', 'synonym'],
    threshold: 0.3
  };

  private languageStore: LanguageStore;
  private routeStore: RouteStore;
  private disposeLiveUpdate: () => void;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.routeStore = rootStore.routeStore;
  }

  public subscribe(segment: Segment = SEGMENT_PRIVATE) {
    if (!this.disposeLiveUpdate) {
      this.disposeLiveUpdate = autorun(() => this.populateCountryList(segment));
    }

    this.navigationReaction = reaction(
      () => this.routeStore.decodedParams,
      (decodedParams) => this.reactToNavigation(decodedParams)
    );
  }

  public unsubscribe() {
    if (this.disposeLiveUpdate) {
      this.disposeLiveUpdate();
      delete this.disposeLiveUpdate;
    }

    this.navigationReaction();
  }

  @computed
  get numberSuggestionItems() {
    return this.numberSuggestionSearchTerm && this.shouldShowSuggestions(this.numberSuggestionSearchTerm) ? this.currentNumberSuggestions.slice() : [];
  }

  public handleInputChange(input: string) {
    input = this.prettifyNumericInput(input);
    runInAction(() => {
      this.numberSuggestionSearchTerm = input;
    });
    if (!!this.selectedCountry && this.selectedCountry.searchTerm !== this.numberSuggestionSearchTerm) {
      runInAction(() => {
        this.selectedCountryImage = null;
      });
    }
    this.noResults = false;
    if (!input || !this.shouldShowSuggestions(input)) {
      return;
    }
    runInAction(() => {
      this.currentNumberSuggestions = isNumeric(input) ? this.getCountrySuggestionsByCallingCode(input) : getFuseResults(this.fuzzySearch.search(input));
    });
  }

  public async fetchCountryList(segment: Segment = SEGMENT_PRIVATE, lang: Language = this.languageStore.currentLanguage) {
    runInAction(() => {
      this.countryList.status = 'FETCHING';
    });
    return CommonAPI.get<{ countries: CountryWithPrices[] }>(`/roaming/countries-with-prices?segment=${segment}&lang=${lang}`, { timeout: 5000 });
  }

  public async fetchPricesByPrefix(prefix: string, segment: Segment = SEGMENT_PRIVATE, lang: Language = this.languageStore.currentLanguage) {
    runInAction(() => {
      this.priceByPrefixList.status = 'FETCHING';
    });
    return CommonAPI.get<{ prices: PriceByPrefix[] }>(`/roaming/prices-by-prefix?prefix=${encodeURIComponent(prefix)}&segment=${segment}&lang=${lang}`, {
      timeout: 5000
    });
  }

  @action
  public selectNumberSuggestionItem(numberSuggestionItem: ISuggestionItem) {
    this.selectedCountry = this.getCountryByIsoCode(numberSuggestionItem.id);
    this.selectedCountryImage = {
      src: `${FLAG_IMAGE_BASEURL}${this.selectedCountry.isoCode}`,
      role: 'presentation'
    };
    this.selectedCountry.searchTerm = getCountryLabel(this.selectedCountry);
    this.routeStore.push(this.routeStore.pathname, {
      isoCode: this.selectedCountry.isoCode
    });
    this.setScrollToResults(true);
  }

  @action
  public handleSubmit(segment: Segment = SEGMENT_PRIVATE) {
    this.noResults = false;
    if (this.currentNumberSuggestions.length === 0) {
      if (!this.selectedCountry || this.selectedCountry.searchTerm !== this.numberSuggestionSearchTerm) {
        return this.getPricesBySearchTerm(clearWhitespace(this.numberSuggestionSearchTerm), segment);
      }
    }
    this.selectNumberSuggestionItem(this.currentNumberSuggestions[0]);
  }

  @computed
  public get isCountryListLoading() {
    return this.countryList.status === 'FETCHING';
  }

  @computed
  public get isPriceByPrefixListLoading() {
    return this.priceByPrefixList.status === 'FETCHING';
  }

  private getPricesBySearchTerm = async (searchTerm: string, segment: Segment = SEGMENT_PRIVATE) => {
    try {
      const response = await this.fetchPricesByPrefix(searchTerm, segment);
      runInAction(() => {
        this.priceByPrefixList.prices = response.data.prices;
        this.selectedCountry = this.mapPricesByPrefixToCountry(this.priceByPrefixList.prices);
        this.selectedCountryImage = this.selectedCountry
          ? {
              src: `${FLAG_IMAGE_BASEURL}${this.selectedCountry.isoCode}`,
              role: 'presentation'
            }
          : null;
        this.noResults = !this.selectedCountry;
        this.priceByPrefixList.status = 'FETCHED';
        if (this.selectedCountry) {
          this.routeStore.push(this.routeStore.pathname, {
            isoCode: this.selectedCountry.isoCode
          });
          this.setScrollToResults(true);
        }
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.priceByPrefixList.status = 'FAILED';
      });
    }
  };

  public populateCountryList = async (segment: Segment = SEGMENT_PRIVATE) => {
    try {
      const response = await this.fetchCountryList(segment);
      runInAction(() => {
        this.countryList.countries = response.data.countries.sort(sortCountries);
        this.allNumberSuggestions = this.countryList.countries.map((country) => this.mapCountryToNumberSuggestion(country));
        this.countryList.status = 'FETCHED';
        this.fuzzySearch = new Fuse(this.allNumberSuggestions, this.fuzzySearchOptions);
        const params = this.routeStore.decodedParams;
        if (params.isoCode) {
          this.selectCountryByIsoCode(params);
        } else if (this.selectedCountry) {
          const updatedCountry = this.getCountryByIsoCode(this.selectedCountry.isoCode);
          this.selectedCountry.name = updatedCountry.name;
          if (!this.selectedCountry.fromPrefix) {
            this.numberSuggestionSearchTerm = getCountryLabel(this.selectedCountry);
            this.selectedCountry.searchTerm = this.numberSuggestionSearchTerm;
          }
        }
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.countryList.status = 'FAILED';
      });
    }
  };

  public reactToNavigation(params: any) {
    if (params.isoCode && (!this.selectedCountry || params.isoCode !== this.selectedCountry.isoCode)) {
      this.selectCountryByIsoCode(params);
    } else if (!params.isoCode) {
      this.deselectCountry();
    }
  }

  public selectCountryByIsoCode(params: any) {
    const countryByIsoCode = this.getCountryByIsoCode(params.isoCode);
    if (countryByIsoCode) {
      runInAction(() => {
        this.selectedCountry = countryByIsoCode;
        this.selectedCountryImage = {
          src: `${FLAG_IMAGE_BASEURL}${this.selectedCountry.isoCode}`,
          role: 'presentation'
        };
        this.numberSuggestionSearchTerm = getCountryLabel(this.selectedCountry);
        this.selectedCountry.searchTerm = this.numberSuggestionSearchTerm;
        this.currentNumberSuggestions = getFuseResults(this.fuzzySearch.search(this.numberSuggestionSearchTerm));
        this.setScrollToResults(true);
      });
    }
  }

  @action
  public deselectCountry() {
    this.selectedCountry = null;
    this.numberSuggestionSearchTerm = '';
  }

  @action
  public setScrollToResults(shouldScroll: boolean) {
    this.scrollToResults = shouldScroll;
  }

  private getCountrySuggestionsByCallingCode(input: string): CallingCodeSuggestion[] {
    return this.countryList.countries
      .filter((country) => `+${country.callingCode}`.startsWith(input))
      .map((country) => this.mapCountryToNumberSuggestion(country));
  }

  private getCountryByIsoCode(isoCode: string) {
    return this.countryList.countries.find((country) => country.isoCode === isoCode);
  }

  private mapCountryToNumberSuggestion(country: CountryWithPrices, lang: Language = this.languageStore.currentLanguage): CallingCodeSuggestion {
    return mapCountryToNumberSuggestion(country, lang);
  }

  private mapPricesByPrefixToCountry(prices: PriceByPrefix[]): CountryWithPrices {
    return prices.length > 0
      ? {
          isoCode: prices[0].isoCode,
          callingCode: this.getCountryByIsoCode(prices[0].isoCode).callingCode,
          name: prices[0].country,
          prices: prices as ServicePrice[],
          fromPrefix: true,
          searchTerm: this.numberSuggestionSearchTerm,
          eea: prices[0].eea
        }
      : null;
  }

  private prettifyNumericInput(input: string) {
    if (input && isNumeric(input) && !input.startsWith('+')) {
      return `+${input}`;
    }
    return input;
  }

  private shouldShowSuggestions(input: string) {
    return !!input && input !== '+' && (!isNumeric(input) || input === '+1' || input === '+7' || input.length > 2);
  }
}

export default RoamingNumberStore;
